import React from "react";
import { useHistory } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useThemeContext } from "../../../../contexts/ThemeContext/ThemeContext";
import { safeLocaleText } from "../PartnerB2B/utils/safeLocaleText";
import SubnetService from "../../../../service/SubnetService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";

const SERVICES_FOR_BUSINESS = [
  { id: "1", title: "Consulenza fiscale e legale", subtitle: "" },
  {
    id: "2",
    title: "Assessment aziendale",
    subtitle: "manager a progetto per obiettivi specifici",
  },
  { id: "3", title: "Bilancio di sostenibilità", subtitle: "e supporto ESG" },
  {
    id: "4",
    title: "Finanza agevolata e partecipazione a bandi",
    subtitle: "regionali, nazionali, europei",
  },
  {
    id: "5",
    title: "Formazione finanziata",
    subtitle: "piani formativi per dipendenti e management",
  },
  { id: "6", title: "Selezione del personale", subtitle: "" },
  { id: "7", title: "Somministrazione di personale", subtitle: "attraverso agenzie per il lavoro" },
  { id: "8", title: "Welfare aziendale e sanitario", subtitle: "" },
  { id: "9", title: "Rating di legalità", subtitle: "attraverso agenzie per il lavoro" },
  { id: "10", title: "Web marketing e costruzione siti internet", subtitle: "" },
  { id: "11", title: "Assistenza per posizionamento SEO", subtitle: "e campagne ADV" },
  {
    id: "12",
    title: "Temporary management",
    subtitle: "manager a progetto per obiettivi specifici",
  },
  { id: "13", title: "Internazionalizzazione", subtitle: "e apertura a mercati esteri" },
  { id: "14", title: "Digitalizzazione aziendale", subtitle: "e implementazione CRM/ERP" },
  { id: "15", title: "Servizi di logistica e supply chain management", subtitle: "" },
  {
    id: "16",
    title: "Investimenti in oro e crypto",
    subtitle: "diversificazione del portafoglio aziendale",
  },
  { id: "17", title: "Business Model Canvas", subtitle: "" },
];

const SERVICES_FOR_INDIVIDUALS = [
  {
    id: "1",
    title: "Assistenza fiscale e commerciale",
    subtitle: "730, dichiarazioni, partita IVA, ecc.",
  },
  {
    id: "2",
    title: "Formazione e accompagnamento al lavoro",
    subtitle: "orientamento, coaching, inserimento",
  },
  { id: "3", title: "Selezione e collocamento in aziende partner", subtitle: "" },
  { id: "4", title: "Lavora con noi come Promoter", subtitle: "" },
  { id: "5", title: "Assistenza e consulenza per luce e gas", subtitle: "" },
  {
    id: "6",
    title: "Accompagnamento all'apertura di attività lavorative",
    subtitle: "o start-up individuali",
  },
  { id: "7", title: "Educazione finanziaria e gestione del budget familiare", subtitle: "" },
  { id: "8", title: "Consulenza per pratiche previdenziali e pensionistiche", subtitle: "" },
  { id: "9", title: "Servizi di traduzione e supporto per pratiche burocratiche", subtitle: "" },
  { id: "10", title: "Formazione digitale", subtitle: "" },
  { id: "11", title: "Servizi assicurativi personalizzati", subtitle: "" },
  { id: "12", title: "Investimenti in oro e crypto", subtitle: "" },
];

const ServiceCard = ({ service, onActivate }) => (
  <div
    className="p-3 border-round-md surface-card"
    style={{
      border: "1px solid var(--theme-border, #eaeaea)",
      backgroundColor: "var(--theme-background, #fff)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <div className="font-semibold mb-1" style={{ color: "var(--theme-text, #171717)" }}>
      {service.title}
    </div>
    {service.subtitle && (
      <div className="text-sm mb-2" style={{ color: "var(--theme-text-muted, #5C5C5C)", flex: 1 }}>
        {service.subtitle}
      </div>
    )}
    <div className="mt-auto">
      <Button
        label={
          service.actionLabel || safeLocaleText("partnerServicesActivateService", "Ativar serviço")
        }
        className="p-button-text p-0 p-no-outline"
        style={{ color: service.actionColor || "var(--theme-primary, #1DAF61)" }}
        disabled={Boolean(service.actionDisabled)}
        loading={Boolean(service.actionLoading)}
        onClick={() => onActivate && onActivate(service)}
      />
    </div>
  </div>
);

const PartnerServicesPage = () => {
  const history = useHistory();
  const { subnetInfo } = useThemeContext();
  const subnetService = new SubnetService();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [activatingServices, setActivatingServices] = React.useState({});
  const [pendingFeedbackServices, setPendingFeedbackServices] = React.useState({});

  const handleActivateService = async (serviceKey, serviceTitle) => {
    if (pendingFeedbackServices[serviceKey] || activatingServices[serviceKey]) {
      return;
    }

    setActivatingServices((prev) => ({ ...prev, [serviceKey]: true }));

    try {
      const response = await subnetService.interestedInService(
        localStorage.getItem("__extraconomy_token"),
        serviceTitle,
        subnetInfo?.id,
      );
      const messageCode = Number(response?.messageCode);

      if (messageCode === 200000) {
        setPendingFeedbackServices((prev) => ({ ...prev, [serviceKey]: true }));
        showSuccessToast({
          detail: "partnerServicesServiceActivated",
        });
      } else {
        showErrorToast({
          code: messageCode || 500000,
          detail: "partnerServicesServiceActivationFailed",
        });
      }
    } catch (error) {
      showErrorToast({
        code: 500000,
        detail: "partnerServicesServiceActivationFailed",
      });
    } finally {
      setActivatingServices((prev) => ({ ...prev, [serviceKey]: false }));
    }
  };

  if (!subnetInfo) {
    return (
      <DashboardLayout>
        <nav
          className="flex align-items-center gap-2 mb-2 text-sm"
          style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
          aria-label="Breadcrumb"
        >
          <span style={{ color: "var(--theme-text, #171717)" }}>
            {safeLocaleText("partnerServicesTitle", "Serviços para parceiros")}
          </span>
        </nav>
        <div
          className="p-4 border-round"
          style={{
            backgroundColor: "var(--theme-border, #eaeaea)",
            color: "var(--theme-text-muted, #5C5C5C)",
          }}
        >
          <p className="m-0">
            {safeLocaleText(
              "partnerB2bSubnetOnly",
              "This page is only available for subnet members.",
            )}
          </p>
          <button
            type="button"
            className="p-button p-button-text mt-2"
            style={{ color: "var(--theme-primary, #1DAF61)" }}
            onClick={() => history.push("/dashboard")}
          >
            {safeLocaleText("userDashboard", "Dashboard")}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <nav
        className="flex align-items-center gap-2 mb-2 text-sm"
        style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
        aria-label="Breadcrumb"
      >
        <span style={{ color: "var(--theme-text, #171717)" }}>
          {safeLocaleText("partnerServicesTitle", "Serviços para parceiros")}
        </span>
      </nav>
      <div className="mb-4">
        <h1 className="text-xl font-semibold m-0" style={{ color: "var(--theme-text, #171717)" }}>
          {safeLocaleText("partnerServicesTitle", "Serviços para parceiros")}
        </h1>
      </div>

      <TabView>
        <TabPanel header={safeLocaleText("partnerServicesTabBusiness", "Serviços para empresas")}>
          <div className="grid">
            {SERVICES_FOR_BUSINESS.map((service) => (
              <div key={`business-${service.id}`} className="col-12 md:col-6 lg:col-4">
                <ServiceCard
                  service={{
                    ...service,
                    actionLabel: pendingFeedbackServices[`business-${service.id}`]
                      ? safeLocaleText("partnerServicesAwaitingFeedback", "Aguardando retorno")
                      : safeLocaleText("partnerServicesActivateService", "Ativar serviço"),
                    actionColor: pendingFeedbackServices[`business-${service.id}`]
                      ? "var(--theme-secondary, #433AC8)"
                      : "var(--theme-primary, #1DAF61)",
                    actionDisabled:
                      pendingFeedbackServices[`business-${service.id}`] ||
                      activatingServices[`business-${service.id}`],
                    actionLoading: activatingServices[`business-${service.id}`],
                  }}
                  onActivate={() => handleActivateService(`business-${service.id}`, service.title)}
                />
              </div>
            ))}
          </div>
        </TabPanel>
        <TabPanel
          header={safeLocaleText("partnerServicesTabIndividuals", "Serviços para particulares")}
        >
          <div className="grid">
            {SERVICES_FOR_INDIVIDUALS.map((service) => (
              <div key={`individual-${service.id}`} className="col-12 md:col-6 lg:col-4">
                <ServiceCard
                  service={{
                    ...service,
                    actionLabel: pendingFeedbackServices[`individual-${service.id}`]
                      ? safeLocaleText("partnerServicesAwaitingFeedback", "Aguardando retorno")
                      : safeLocaleText("partnerServicesActivateService", "Ativar serviço"),
                    actionColor: pendingFeedbackServices[`individual-${service.id}`]
                      ? "var(--theme-secondary, #433AC8)"
                      : "var(--theme-primary, #1DAF61)",
                    actionDisabled:
                      pendingFeedbackServices[`individual-${service.id}`] ||
                      activatingServices[`individual-${service.id}`],
                    actionLoading: activatingServices[`individual-${service.id}`],
                  }}
                  onActivate={() =>
                    handleActivateService(`individual-${service.id}`, service.title)
                  }
                />
              </div>
            ))}
          </div>
        </TabPanel>
      </TabView>
    </DashboardLayout>
  );
};

export default PartnerServicesPage;
